// Dev-only lightweight boot timing logger.
//
// Emits `[boot] <label> +<delta>ms (total +<elapsed>ms)` to console only when
// running in dev (Vite `import.meta.env.DEV`). In production builds the
// helpers compile down to no-op calls because the env check is constant.
//
// Intended for short-lived diagnostic use during boot/reload investigations.
// Safe to leave in tree; remove once boot timing is no longer interesting.

const IS_DEV = import.meta.env.DEV;

const NAV_TIMING: PerformanceNavigationTiming | undefined =
  typeof performance !== "undefined" &&
  typeof performance.getEntriesByType === "function"
    ? (performance.getEntriesByType("navigation")[0] as
        | PerformanceNavigationTiming
        | undefined)
    : undefined;

const BOOT_T0_MS = typeof performance !== "undefined" ? performance.now() : 0;

let lastMarkMs = BOOT_T0_MS;

function nowMs(): number {
  return typeof performance !== "undefined" ? performance.now() : 0;
}

export function bootMark(label: string): void {
  if (!IS_DEV) return;
  const now = nowMs();
  const total = now - BOOT_T0_MS;
  const delta = now - lastMarkMs;
  lastMarkMs = now;
  // eslint-disable-next-line no-console
  console.info(
    `[boot] ${label} +${delta.toFixed(1)}ms (total ${total.toFixed(1)}ms)`,
  );
}

export async function bootMeasureAsync<T>(
  label: string,
  task: () => Promise<T>,
): Promise<T> {
  if (!IS_DEV) return task();
  const start = nowMs();
  try {
    return await task();
  } finally {
    const elapsed = nowMs() - start;
    // eslint-disable-next-line no-console
    console.info(`[boot] ${label} took ${elapsed.toFixed(1)}ms`);
  }
}

export function bootLogNavigationOrigin(): void {
  if (!IS_DEV) return;
  if (NAV_TIMING === undefined) {
    // eslint-disable-next-line no-console
    console.info("[boot] navigation timing unavailable");
    return;
  }
  // Times relative to navigationStart (PerformanceNavigationTiming startTime
  // is 0 by spec). responseEnd ≈ HTML received; domContentLoadedEventEnd ≈
  // DOM ready; loadEventEnd ≈ window.load. These help split network/parse vs
  // module-script time, especially for `npm run dev` where Vite transforms
  // are issued from the module script.
  const t = NAV_TIMING;
  // eslint-disable-next-line no-console
  console.info(
    `[boot] nav-timing responseEnd=${t.responseEnd.toFixed(1)}ms domContentLoaded=${t.domContentLoadedEventEnd.toFixed(1)}ms`,
  );
}

export function bootGetT0(): number {
  return BOOT_T0_MS;
}
